import React, { useState, useEffect } from 'react';
import {
  ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer
} from 'recharts';
import api from '../api';

function euro(v) { return Number(v || 0).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' }); }
function fmtDate(s) { if (!s) return '—'; const [y,m,d] = s.split('-'); return `${d}.${m}.${y}`; }

const CONF_COLOR = { HOCH: '#10b981', MITTEL: '#f59e0b', NIEDRIG: '#ef4444', KEINE: '#94a3b8' };

function SummaryCard({ days, data }) {
  const netto = data?.netto ?? 0;
  return (
    <div className="an-card" style={{ textAlign: 'center' }}>
      <div style={{ color: '#64748b', fontSize: 11, fontWeight: 700, textTransform: 'uppercase' }}>
        {days} Tage
      </div>
      <div style={{ fontSize: 20, fontWeight: 700, color: netto >= 0 ? '#10b981' : '#ef4444', marginTop: 6 }}>
        {netto >= 0 ? '+' : ''}{euro(netto)}
      </div>
      <div style={{ fontSize: 11, color: '#64748b', marginTop: 4 }}>
        +{euro(data?.einnahmen)} / −{euro(data?.ausgaben)}
        <br/>{data?.ereignisse || 0} Ereignis(se)
      </div>
    </div>
  );
}

function ForecastTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: 8, padding: '8px 12px', fontSize: 12 }}>
      <div style={{ fontWeight: 700, marginBottom: 4 }}>{fmtDate(label)}</div>
      {payload.map((p, i) => (
        <div key={i} style={{ color: p.color }}>{p.name}: {euro(p.value)}</div>
      ))}
    </div>
  );
}

export default function ForecastPage() {
  const [forecast, setForecast] = useState(null);
  const [fixkosten, setFixkosten] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    async function load() {
      setLoading(true);
      setError('');
      try {
        const [f, fx] = await Promise.all([
          api.get('/forecast', { params: { days: 90 } }),
          api.get('/fixkosten/summary')
        ]);
        setForecast(f.data);
        setFixkosten(fx.data);
      } catch (e) {
        setError(e.response?.data?.error || e.message);
      } finally { setLoading(false); }
    }
    load();
  }, []);

  if (loading) return <div style={{ color: '#64748b', padding: 20, fontSize: 13 }}>Lade Prognose...</div>;
  if (error) return <div style={{ color: '#ef4444', padding: 20, fontSize: 13 }}>Fehler: {error}</div>;
  if (!forecast) return null;

  const confColor = CONF_COLOR[forecast.forecastConfidence] || '#94a3b8';
  const sum30 = forecast.summaries?.find(s => s.tage === 30);
  const sum60 = forecast.summaries?.find(s => s.tage === 60);
  const sum90 = forecast.summaries?.find(s => s.tage === 90);

  // Build chart data from timeline (daily net only)
  const chartData = (forecast.timeline || []).map(e => ({
    datum: e.datum,
    cumulative: e.cumulative,
    betrag: e.betrag
  }));

  return (
    <div>
      <h1 style={{ margin: '0 0 4px', fontSize: 20, color: '#1e293b' }}>Voraussichtlicher Cashflow</h1>
      <p style={{ margin: '0 0 14px', fontSize: 12, color: '#64748b' }}>
        Basiert ausschließlich auf bestätigten wiederkehrenden Zahlungen — keine Prognose zukünftiger Einzelkäufe.
        Basis: Daten bis {fmtDate(forecast.lastDatum)}.
      </p>

      <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: '8px 14px', marginBottom: 14, display: 'flex', gap: 12, alignItems: 'center' }}>
        <span style={{ fontWeight: 700, fontSize: 12, color: confColor }}>
          Prognose-Qualität: {forecast.forecastConfidence}
        </span>
        <span style={{ fontSize: 12, color: '#64748b' }}>
          {forecast.confirmed} bestätigte Serie(n) · {forecast.confidenceGrund}
        </span>
      </div>

      {forecast.confirmed === 0 ? (
        <div style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: 10, padding: 24, textAlign: 'center', color: '#64748b', fontSize: 13 }}>
          Noch keine bestätigten wiederkehrenden Zahlungen vorhanden.
          <br/>Gehe zu <strong>Wiederkehrend</strong> und bestätige Kandidaten.
        </div>
      ) : (
        <>
          <div className="an-kpi-grid" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
            <SummaryCard days={30} data={sum30} />
            <SummaryCard days={60} data={sum60} />
            <SummaryCard days={90} data={sum90} />
          </div>

          {chartData.length > 0 && (
            <div className="an-card" style={{ marginBottom: 12 }}>
              <div style={{ fontWeight: 700, fontSize: 13, color: '#1e293b', marginBottom: 10 }}>
                Kumulative Nettoveränderung (90 Tage)
              </div>
              <div style={{ fontSize: 11, color: '#64748b', marginBottom: 8 }}>
                Kein tatsächlicher Kontostand — erwartete Nettoveränderung auf Basis bestätigter Serien.
              </div>
              <ResponsiveContainer width="100%" height={200}>
                <ComposedChart data={chartData} margin={{ top: 4, right: 8, left: 0, bottom: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="datum" tick={{ fontSize: 10 }} tickFormatter={s => s ? s.slice(5) : ''} />
                  <YAxis tick={{ fontSize: 10 }} tickFormatter={v => `${Math.round(v)}€`} />
                  <Tooltip content={<ForecastTooltip />} />
                  <Bar dataKey="betrag" name="Ereignis" fill="#94a3b8" opacity={0.6} />
                  <Line dataKey="cumulative" name="Kumulativ" stroke="#6366f1" strokeWidth={2} dot={false} />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          )}

          <div className="an-2col">
            <div className="an-card">
              <div style={{ fontWeight: 700, fontSize: 13, color: '#1e293b', marginBottom: 10 }}>
                Erwartete Zahlungen (90 Tage)
              </div>
              {forecast.timeline.length === 0 ? (
                <div style={{ color: '#64748b', fontSize: 12 }}>Keine erwarteten Zahlungen.</div>
              ) : (
                <table className="an-table">
                  <thead><tr>
                    <th>Datum</th><th>Bezeichnung</th><th>Betrag</th>
                  </tr></thead>
                  <tbody>
                    {forecast.timeline.slice(0, 30).map((e, i) => (
                      <tr key={i}>
                        <td style={{ color: '#64748b', whiteSpace: 'nowrap' }}>{fmtDate(e.datum)}</td>
                        <td>
                          <span style={{ fontSize: 12 }}>{e.bezeichnung}</span>
                          <br/>
                          <span style={{ fontSize: 10, color: '#94a3b8' }}>{e.kategorie}</span>
                        </td>
                        <td style={{ textAlign: 'right', fontWeight: 700, color: e.betrag < 0 ? '#ef4444' : '#10b981', whiteSpace: 'nowrap' }}>
                          {e.isVariable ? 'ca. ' : ''}
                          {e.betrag > 0 ? '+' : ''}{euro(e.betrag)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            <div className="an-card">
              <div style={{ fontWeight: 700, fontSize: 13, color: '#1e293b', marginBottom: 10 }}>Fixkosten-Übersicht</div>
              {!fixkosten ? (
                <div style={{ color: '#64748b', fontSize: 12 }}>Keine Daten.</div>
              ) : (
                <>
                  <div style={{ marginBottom: 10 }}>
                    <div style={{ fontSize: 11, color: '#64748b', textTransform: 'uppercase', fontWeight: 700 }}>Monatliches Äquivalent</div>
                    <div style={{ fontSize: 20, fontWeight: 700, color: '#ef4444', marginTop: 2 }}>
                      {euro(fixkosten.fixkosten?.monatlichAequivalent)}
                    </div>
                    <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>
                      Jahreshochrechnung: {euro(fixkosten.fixkosten?.jaehrlichHochrechnung)}
                    </div>
                    {fixkosten.fixkosten?.anteilEinnahmen != null && (
                      <div style={{ fontSize: 11, color: '#f59e0b', marginTop: 2 }}>
                        Anteil Einnahmen: {fixkosten.fixkosten.anteilEinnahmen} %
                      </div>
                    )}
                    {fixkosten.hinweis && (
                      <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4, fontStyle: 'italic' }}>
                        {fixkosten.hinweis}
                      </div>
                    )}
                  </div>
                  {fixkosten.fixkosten?.serien?.map((s, i) => (
                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', borderTop: '1px solid #f1f5f9', fontSize: 12 }}>
                      <span>{s.bezeichnung}</span>
                      <span style={{ color: '#ef4444', fontWeight: 700 }}>{euro(s.erwarteter_betrag)}</span>
                    </div>
                  ))}
                  {fixkosten.einnahmen?.serien?.length > 0 && (
                    <>
                      <div style={{ marginTop: 12, fontWeight: 700, fontSize: 12, color: '#10b981' }}>
                        Regelmäßige Einnahmen ({euro(fixkosten.einnahmen.monatlichAequivalent)}/Monat)
                      </div>
                      {fixkosten.einnahmen.serien.map((s, i) => (
                        <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', borderTop: '1px solid #f1f5f9', fontSize: 12 }}>
                          <span>{s.bezeichnung}</span>
                          <span style={{ color: '#10b981', fontWeight: 700 }}>{euro(s.erwarteter_betrag)}</span>
                        </div>
                      ))}
                    </>
                  )}
                </>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
